import { Table } from '@tanstack/react-table';
import { RefObject, useEffect, useRef } from 'react';

// Clamp-enabled text columns are capped so their content actually wraps to multiple lines
// (and thus overflows the 3-line clamp, revealing the expand affordance). Without a cap a
// long single-line value measured under auto-layout would freeze the column wide enough that
// nothing ever clamps.
const CLAMP_COLUMN_MAX_WIDTH = 320;

/**
 * Freezes column widths on mount, then flips the table to `table-layout: fixed`.
 *
 * Width source, per column:
 *  - an explicit `size` in the column def wins (lets a table pin e.g. the name column wide);
 *  - otherwise the as-rendered `<th>` DOM width is used, capped for clamp columns.
 *
 * Measures live DOM only — never samples the dataset (that heuristic is scoped to the
 * auxiliary metrics table and must not be generalized here).
 */
export const useFreezeColumnWidths = <TData>(
  containerRef: RefObject<HTMLElement>,
  table: Table<TData>,
  onColumnSizingChange: (sizing: Record<string, number>) => void,
  // Re-attempts measurement as rows arrive — headers can render at 0 width before the
  // first real paint (e.g. while a loader gate hides the body).
  rowCount: number,
) => {
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current) return;
    const container = containerRef.current;
    if (!container) return;

    const headerCells = container.querySelectorAll<HTMLElement>('thead th[data-column-id]');
    if (headerCells.length === 0) return;

    const sizing: Record<string, number> = {};
    let hasMeasurableWidth = false;
    headerCells.forEach(cell => {
      const { columnId } = cell.dataset;
      if (!columnId) return;

      const column = table.getColumn(columnId);
      const explicitSize = column?.columnDef.size;
      if (typeof explicitSize === 'number') {
        sizing[columnId] = explicitSize;
        hasMeasurableWidth = true;
        return;
      }

      const { width } = cell.getBoundingClientRect();
      if (width <= 0) return;
      hasMeasurableWidth = true;
      sizing[columnId] = column?.columnDef.meta?.clamp ? Math.min(width, CLAMP_COLUMN_MAX_WIDTH) : width;
    });

    if (!hasMeasurableWidth) return;

    seededRef.current = true;
    onColumnSizingChange(sizing);
    // table is a mutable TanStack instance — not a reactive dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, onColumnSizingChange, rowCount]);

  return seededRef.current;
};
